import { create } from 'zustand';

import { EventTimetableOption, EventType, EventTypesState } from '@event/types';
import IconCulture from '@ui/components/icons/IconCulture';
import IconMeeting from '@ui/components/icons/IconMeeting';
import IconParty from '@ui/components/icons/IconParty';
import IconPub from '@ui/components/icons/IconPub';
import IconSport from '@ui/components/icons/IconSport';

const getFullDayTimetableOption = (id: number): EventTimetableOption => {
  return { id, name: 'Celý den', duration: 24, fullDay: true };
};

const getHalfDayTimetableOption = (id: number): EventTimetableOption => {
  return { id, name: 'Půl dne', duration: 12 };
};

const getTwoHoursTimetableOption = (id: number): EventTimetableOption => {
  return { id, name: 'Dvě hodiny', duration: 2 };
};

const getOneHourTimetableOption = (id: number): EventTimetableOption => {
  return { id, name: 'Hodina', duration: 1 };
};

const getTillMidnightTimetableOption = (id: number): EventTimetableOption => {
  return { id, name: 'Do zavíračky', tillMidnight: true };
};

const allEventTypes = [
  {
    id: 1,
    name: 'Hospoda',
    iconComponent: IconPub,
    typeEnumValue: 'PUB',
    timetableOptions: [
      getTillMidnightTimetableOption(1),
      getHalfDayTimetableOption(2),
      getOneHourTimetableOption(3),
      getTwoHoursTimetableOption(4),
    ],
  },
  {
    id: 2,
    name: 'Setkání',
    iconComponent: IconMeeting,
    typeEnumValue: 'MEETING',
    timetableOptions: [
      getTwoHoursTimetableOption(5),
      getHalfDayTimetableOption(6),
      getOneHourTimetableOption(7),
      getFullDayTimetableOption(8),
    ],
  },
  {
    id: 3,
    name: 'Sport',
    iconComponent: IconSport,
    typeEnumValue: 'SPORT',
    timetableOptions: [
      getOneHourTimetableOption(9),
      getTwoHoursTimetableOption(10),
      getHalfDayTimetableOption(11),
      getFullDayTimetableOption(12),
    ],
  },
  {
    id: 4,
    name: 'Kultura',
    iconComponent: IconCulture,
    typeEnumValue: 'CULTURE',
    timetableOptions: [
      getTwoHoursTimetableOption(13),
      getHalfDayTimetableOption(14),
      getOneHourTimetableOption(15),
      getFullDayTimetableOption(16),
    ],
  },
  {
    id: 5,
    name: 'Párty',
    iconComponent: IconParty,
    typeEnumValue: 'PARTY',
    timetableOptions: [
      getHalfDayTimetableOption(17),
      getFullDayTimetableOption(18),
      getOneHourTimetableOption(19),
      getTwoHoursTimetableOption(20),
    ],
  },
] as const;

const initEventsUpdate = (): EventType[] => {
  return allEventTypes.map((eventType) => {
    return {
      ...eventType,
      isSelected: eventType.id === allEventTypes[0].id,
      timetableOptions: eventType.timetableOptions.map(
        (timetableOption, index) => {
          return {
            ...timetableOption,
            isSelected: index === 0,
          };
        },
      ),
    };
  });
};

const useEventTypesStore = create<EventTypesState>()((set, get) => ({
  eventsType: get()?.eventsType || initEventsUpdate(),
  setEventsType: (eventsType) => set({ eventsType }),
  selectedEventType:
    get()?.selectedEventType || get()?.eventsType[0] || initEventsUpdate()[0],
  setSelectedEventType(selectedEventType) {
    set(() => ({ selectedEventType }));
  },
  selectedEventTimeslotLength:
    get()?.selectedEventType?.timetableOptions[0] || null,
  setSelectedEventTimeslotLength(selectedEventTimeslotLength) {
    set(() => ({ selectedEventTimeslotLength }));
  },
  getByTypeEnumValue(typeEnumValue) {
    return (
      get()?.eventsType.find(
        (eventType) => eventType.typeEnumValue === typeEnumValue,
      ) || null
    );
  },
}));

export default useEventTypesStore;
